import {createContext, useContext} from 'react';
import {createStore, StoreApi, useStore} from 'zustand';
import {
  AccountFilterCondition,
  AccountFilter,
} from '../../shared/filter/accountFilter';
import {track} from '../../analytics';
import {
  type FilterItemWithId,
  isAccountFilterGroupDraft,
  isFilterItemSet,
  accountFilterToFilterItems,
} from './filterTypes';

export interface FilterState {
  // Data
  filterItems: FilterItemWithId[];
  filterOperator: 'and' | 'or';
  excluded: boolean;
  disqualified: boolean;
  search: string;

  // Condition operations
  addEmptyFilterCondition: () => void;
  updateFilterCondition: (
    itemId: string,
    condition: AccountFilterCondition
  ) => void;
  removeFilterItem: (itemId: string) => void;

  // Group operations
  addEmptyFilterGroup: () => void;
  addConditionToGroup: (groupId: string) => void;
  updateConditionInGroup: (
    groupId: string,
    conditionId: string,
    condition: AccountFilterCondition
  ) => void;
  removeConditionFromGroup: (groupId: string, conditionId: string) => void;
  updateFilterGroupOperator: (groupId: string, operator: 'and' | 'or') => void;

  // Top-level operations
  setFilterOperator: (op: 'and' | 'or') => void;
  setExcluded: (v: boolean) => void;
  setDisqualified: (v: boolean) => void;
  setSearch: (text: string) => void;

  // Utilities
  numberOfActiveFiltersToDisplay: () => number;
  filtersDifferFromDefault: () => boolean;
  resetFilters: () => void;
  setFilterState: (filter: AccountFilter) => void;
}

export function createFilterStore(): StoreApi<FilterState> {
  return createStore<FilterState>((set, get) => ({
    filterItems: [],
    filterOperator: 'and',
    excluded: false,
    disqualified: false,
    search: '',

    addEmptyFilterCondition() {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Add Filter Item'},
        });
        return {
          filterItems: [
            ...state.filterItems,
            {id: crypto.randomUUID(), item: undefined},
          ],
        };
      });
    },

    updateFilterCondition(itemId: string, condition: AccountFilterCondition) {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Update Filter Item'},
        });
        return {
          filterItems: state.filterItems.map((item) => {
            if (item.id !== itemId || isAccountFilterGroupDraft(item)) {
              return item;
            }
            return {...item, item: condition};
          }),
        };
      });
    },

    removeFilterItem(itemId: string) {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Remove Filter Item'},
        });
        return {
          filterItems: state.filterItems.filter((item) => item.id !== itemId),
        };
      });
    },

    addEmptyFilterGroup() {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Add Filter Group'},
        });
        return {
          filterItems: [
            ...state.filterItems,
            {
              id: crypto.randomUUID(),
              operator: 'and' as const,
              conditions: [{id: crypto.randomUUID(), item: undefined}],
            },
          ],
        };
      });
    },

    addConditionToGroup(groupId: string) {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Add Condition To Group'},
        });
        return {
          filterItems: state.filterItems.map((item) => {
            if (item.id !== groupId || !isAccountFilterGroupDraft(item)) {
              return item;
            }
            return {
              ...item,
              conditions: [
                ...item.conditions,
                {id: crypto.randomUUID(), item: undefined},
              ],
            };
          }),
        };
      });
    },

    updateConditionInGroup(
      groupId: string,
      conditionId: string,
      condition: AccountFilterCondition
    ) {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Update Condition In Group'},
        });
        return {
          filterItems: state.filterItems.map((item) => {
            if (item.id !== groupId || !isAccountFilterGroupDraft(item)) {
              return item;
            }
            return {
              ...item,
              conditions: item.conditions.map((c) =>
                c.id === conditionId ? {...c, item: condition} : c
              ),
            };
          }),
        };
      });
    },

    removeConditionFromGroup(groupId: string, conditionId: string) {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Remove Condition From Group'},
        });
        const newFilterItems: FilterItemWithId[] = [];
        for (const item of state.filterItems) {
          if (item.id !== groupId || !isAccountFilterGroupDraft(item)) {
            newFilterItems.push(item);
            continue;
          }
          const newConditions = item.conditions.filter(
            (c) => c.id !== conditionId
          );
          // If the group is now empty, remove it entirely
          if (newConditions.length > 0) {
            newFilterItems.push({...item, conditions: newConditions});
          }
        }
        return {filterItems: newFilterItems};
      });
    },

    updateFilterGroupOperator(groupId: string, operator: 'and' | 'or') {
      set((state) => {
        track('accountsFiltered', {
          properties: {filter: 'Update Filter Group'},
        });
        return {
          filterItems: state.filterItems.map((item) => {
            if (item.id !== groupId || !isAccountFilterGroupDraft(item)) {
              return item;
            }
            return {...item, operator};
          }),
        };
      });
    },

    setFilterOperator(operator: 'and' | 'or') {
      track('accountsFiltered', {properties: {filter: 'Operator'}});
      set({filterOperator: operator});
    },

    setExcluded(excluded: boolean) {
      track('accountsFiltered', {properties: {filter: 'Excluded'}});
      set({excluded});
    },

    setDisqualified(disqualified: boolean) {
      track('accountsFiltered', {properties: {filter: 'Disqualified'}});
      set({disqualified});
    },

    setSearch(searchText: string) {
      set({search: searchText});
    },

    numberOfActiveFiltersToDisplay() {
      return get().filterItems.filter(isFilterItemSet).length;
    },

    filtersDifferFromDefault() {
      const state = get();
      const isDefault =
        state.filterItems.filter(isFilterItemSet).length === 0 &&
        !state.excluded &&
        !state.disqualified;
      return !isDefault;
    },

    resetFilters() {
      set({
        filterItems: [],
        filterOperator: 'and',
        search: '',
        excluded: false,
        disqualified: false,
      });
    },

    setFilterState(filter: AccountFilter) {
      const {filterItems, filterOperator} = accountFilterToFilterItems(filter);
      track('accountsFiltered', {
        properties: {filter: 'Set Filter State'},
      });
      set({
        filterItems,
        filterOperator,
        excluded: filter.excluded ?? false,
        disqualified: filter.disqualified ?? false,
        search: filter.search ?? '',
      });
    },
  }));
}

// Context for providing a filter store instance
export const FilterStoreContext = createContext<StoreApi<FilterState> | null>(
  null
);

export function useFilterStore() {
  const store = useContext(FilterStoreContext);
  if (!store) {
    throw new Error('useFilterStore must be used within a FilterStoreProvider');
  }
  return useStore(store);
}
